import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { GungNotificationService } from 'gung-common';
import { ConfigBaseFilter, ConfigService, ListLayout, ListSortOption } from 'gung-list';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { first, map } from 'rxjs';
import { AiOrderParserOrderCardComponent } from '../../components/pdf-ai-parser/pdf-ai-parser-order-card/pdf-ai-parser-order-card.component';
import { AiOrderParserOrderListLayoutComponent } from '../../components/pdf-ai-parser/pdf-ai-parser-order-list-layout/pdf-ai-parser-order-list-layout.component';
import { AiOrderParserItem } from '../../models/ai-order-parser-item';
import { AiOrderParserReviewService } from './ai-order-parser-review.service';

@Injectable({ providedIn: 'root' })
export class AiOrderParserBatchListConfigService implements ConfigService<AiOrderParserItem> {
  private service = inject(AiOrderParserReviewService);
  private notificationService = inject(GungNotificationService);
  private translate = inject(TranslateService);
  private items$ = new BehaviorSubject<AiOrderParserItem[]>([]);
  private loadedSubject = new BehaviorSubject<boolean>(false);
  /** Emits true once the first fetch has completed (used by the inbox empty state). */
  loaded$: Observable<boolean> = this.loadedSubject.asObservable();

  /** Every item id seen so far — lets the inbox highlight rows arriving on the poll. */
  private knownIds = new Set<string>();
  /**
   * Ids deleted this session. The list read can briefly still return a just-deleted
   * item (cached GET / read-replica lag), so we filter these out of every reload until
   * the backend stops returning them, then forget them.
   */
  private deletedIds = new Set<string>();
  /**
   * Items uploaded this session, keyed by id. The list read can briefly not yet include a
   * just-created item (cached GET / read-replica lag), so we keep prepending these to every
   * reload until the backend returns them, then forget them.
   */
  private pendingItems = new Map<string, AiOrderParserItem>();
  /** Ids that arrived on the latest reload; cleared shortly after so re-renders don't replay the highlight. */
  private newIds = new Set<string>();
  private seeded = false;
  /** Set while the list fetch keeps failing, so the poll toasts once per outage instead of every tick. */
  private loadFailed = false;
  private newIdsClearTimer?: ReturnType<typeof setTimeout>;
  /** How long arrived ids stay marked as new (highlight animation is 2s). */
  private static readonly NEW_IDS_TTL_MS = 4_000;

  topFilter = true;
  paginationList = true;
  // The inbox renders its own search box + status chips, so gung-list's built-in
  // search and filter dropdowns are turned off (getFilters returns []); filtering
  // happens client-side here instead.
  searchDisabled = true;

  /** Free-text search + status quick-filter, applied to the list client-side. */
  private searchTerm$ = new BehaviorSubject<string>('');
  private statusFilter$ = new BehaviorSubject<string | null>(null);

  constructor() {
    this.reload();
  }

  /** Re-fetch the queue (e.g. after a manual upload) and push to the list. */
  reload(): void {
    this.service.list().pipe(first()).subscribe({
      next: items => {
        this.loadFailed = false;
        const fetched = items || [];
        const fetchedIds = new Set(fetched.map(item => item.id));
        // Deletion tombstones: keep hiding a deleted id until the backend stops returning it.
        this.deletedIds.forEach(id => {
          if (!fetchedIds.has(id)) {
            this.deletedIds.delete(id);
          }
        });
        // Pending uploads: keep showing a just-uploaded item until the backend list includes it.
        this.pendingItems.forEach((_item, id) => {
          if (fetchedIds.has(id)) {
            this.pendingItems.delete(id);
          }
        });
        const list = [...this.pendingItems.values(), ...fetched].filter(
          item => !this.deletedIds.has(item.id)
        );
        if (this.seeded) {
          const arrived = list.filter(item => !this.knownIds.has(item.id));
          if (arrived.length > 0) {
            arrived.forEach(item => this.newIds.add(item.id));
            clearTimeout(this.newIdsClearTimer);
            this.newIdsClearTimer = setTimeout(
              () => this.newIds.clear(),
              AiOrderParserBatchListConfigService.NEW_IDS_TTL_MS
            );
          }
        }
        list.forEach(item => this.knownIds.add(item.id));
        // First load seeds the known-id set without animating anything.
        this.seeded = true;
        this.items$.next(list);
        this.loadedSubject.next(true);
      },
      error: err => {
        // Keep the current items: a transient poll failure must not wipe the list.
        if (!this.loadFailed) {
          this.loadFailed = true;
          const serverMessage = typeof err.error === 'string' ? err.error : err.error?.message;
          this.notificationService.notify('', serverMessage || this.translate.instant('ERROR'), 'error');
        }
        this.loadedSubject.next(true);
      }
    });
  }

  /** Whether the item arrived on the latest poll (drives the row arrival highlight). */
  isNewItem(id: string): boolean {
    return this.newIds.has(id);
  }

  /**
   * Optimistically add a just-uploaded item to the top of the list so it appears immediately,
   * without waiting for the backend list read to catch up. Reconciled on the next reload.
   */
  addItem(item: AiOrderParserItem): void {
    if (!item?.id) {
      return;
    }
    this.pendingItems.set(item.id, item);
    this.deletedIds.delete(item.id);
    this.knownIds.add(item.id);
    const rest = this.items$.value.filter(existing => existing.id !== item.id);
    this.items$.next([item, ...rest]);
  }

  /**
   * Optimistically drop a deleted item from the loaded list so its row disappears
   * immediately, without waiting for a backend re-fetch. The next poll/reload reconciles.
   */
  removeItem(id: string): void {
    this.deletedIds.add(id);
    this.pendingItems.delete(id);
    this.knownIds.delete(id);
    this.newIds.delete(id);
    this.items$.next(this.items$.value.filter(item => item.id !== id));
  }

  /** Filtered list gung-list renders (free-text search + selected status chip). */
  getItems(): Observable<AiOrderParserItem[]> {
    return combineLatest([this.items$, this.searchTerm$, this.statusFilter$]).pipe(
      map(([items, term, status]) => this.applyClientFilters(items, term, status))
    );
  }

  /** Unfiltered list — the queue stats, badge and empty-state must reflect every item. */
  getRawItems(): Observable<AiOrderParserItem[]> {
    return this.items$.asObservable();
  }

  get statusFilter(): string | null {
    return this.statusFilter$.value;
  }

  setStatusFilter(status: string | null): void {
    this.statusFilter$.next(status);
  }

  setSearchTerm(term: string): void {
    this.searchTerm$.next(term || '');
  }

  private applyClientFilters(
    items: AiOrderParserItem[],
    term: string,
    status: string | null
  ): AiOrderParserItem[] {
    const needle = term.trim().toLowerCase();
    return items.filter(item => {
      if (status && item.status !== status) {
        return false;
      }
      if (needle) {
        const haystack = this.getSearchTerms(item).join(' ').toLowerCase();
        if (!haystack.includes(needle)) {
          return false;
        }
      }
      return true;
    });
  }

  getFilters(): ConfigBaseFilter<AiOrderParserItem>[] {
    return [];
  }

  getSortOptions(): ListSortOption<AiOrderParserItem>[] {
    return [];
  }

  // Single entry hides gung-list's own "Visa N" dropdown (it renders only when
  // there is more than one size); the inbox renders its own next to the search,
  // driving the `limit` query param. batchSizes[0] is the default page size when
  // no `limit` is in the URL, so it must match the inbox select's default (25).
  getBatchSizes(): number[] {
    return [25];
  }

  getLayouts(): ListLayout<AiOrderParserItem>[] {
    return [
      {
        getIconClass: () => '',
        getListItemComponent: () => AiOrderParserOrderCardComponent,
        getListLayoutComponent: () => AiOrderParserOrderListLayoutComponent,
        getName: () => 'Incoming orders'
      }
    ];
  }

  getSearchTerms(item: AiOrderParserItem): string[] {
    const terms: string[] = [];
    if (item.originalFilename) terms.push(item.originalFilename);
    if (item.fromEmail) terms.push(item.fromEmail);
    if (item.uploadedBy) terms.push(item.uploadedBy);
    if (item.subject) terms.push(item.subject);
    const orderNo = item.parsedOrder?.extra?.['customerOrderNumber'];
    if (orderNo) terms.push(orderNo);
    if (item.erpOrderIds?.length) terms.push(...item.erpOrderIds);
    return terms;
  }

  getItemId(item: AiOrderParserItem): string {
    return item.id;
  }

  getSearchPlaceholderTranslate(): string {
    return 'AI_PARSER_SEARCH_PLACEHOLDER';
  }

  // Only reachable when items exist but the filters/search exclude everything —
  // the never-any-orders empty state is rendered by the batch view itself.
  getNothingFoundTranslateTag(): string {
    return 'AI_PARSER_NO_MATCH';
  }
}
